import {
  Person,
  Phone,
  AllInbox,
  Email,
  Event,
  Http,
  QuestionMark,
  AltRoute,
} from '@mui/icons-material';
import { formatRelative, isValid } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import { useTranslation } from 'react-i18next';
import useMiViewTacticCardConfig from './MiViewTacticCard.config';
import { Channel } from 'basics/enums/maya.enums';
import { Tactic, VeevaVaultDocument } from 'generated/maya.types';

const useMiViewTacticCard = (tactic: Tactic, tactics: Tactic[], IVADocuments: VeevaVaultDocument[], AEDocuments: VeevaVaultDocument[]) => {
  const { t } = useTranslation();

  const formatDateToMMDDYYYY = (dateString: string): string => {
    if (!dateString || !isValid(new Date(dateString))) {
      return 'Invalid Date';
    }

    const utcDate = utcToZonedTime(new Date(dateString), 'UTC');
    const nowUtc = utcToZonedTime(new Date(), 'UTC');

    return formatRelative(utcDate, nowUtc);
  };

  const getChannel = () => {
    if (!tactic?.channel) return '';

    switch (Channel[tactic.channel]) {
      case Channel.oneToOneDropMaterial:
      case Channel.oneToOneIVA:
      case Channel.oneToOneKeyMessage:
      case Channel.oneToOne:
      case Channel.f2fMeeting:
        return t('maya_miview_tactic_f2f_channel_label');
      case Channel.group:
        return t('maya_miview_tactic_group_channel_label');
      case Channel.f2fEvent:
        return t('maya_miview_tactic_f2f_event_channel_label');
      case Channel.telephone:
        return t('maya_miview_tactic_telephone_channel_label');
      case Channel.broadcastEmail:
        return t('maya_miview_tactic_broadcast_email_channel_label');
      case Channel.approvedEmail:
        return t('maya_miview_tactic_approved_email_channel_label');
      case Channel.webLogIn:
        return t('maya_miview_tactic_web_login_channel_label');
      case Channel.webNoLogIn:
        return t('maya_miview_tactic_web_no_login_channel_label');
      default:
        return Channel[tactic.channel];
    }
  };

  const getSubtitle = () => {
    if (!tactic) {
      return null;
    }

    let tacticContent: string | null | undefined = '';

    if (tactic?.keyMessage?.name) {
      tacticContent = tactic.keyMessage.name;
    } else if (tactic.approvedDocumentId) {
      const document = AEDocuments?.find((doc) => doc.id === Number(tactic.approvedDocumentId));
      tacticContent = document?.name;
    } else if (tactic.dropMaterial && tactic.dropMaterial?.name) {
      tacticContent = tactic.dropMaterial.name;
    } else if (tactic.IVADocumentId && tactic.IVADocumentId) {
      const document = IVADocuments?.find((doc) => doc.id === Number(tactic.IVADocumentId));
      tacticContent = document?.name;
    } else {
      tacticContent = tactic.assetName;
    }

    return tacticContent || '';
  };

  const extractLastNumber = (formattedId: string): number => {
    if (!formattedId) return 0;
    const parts = formattedId.split('-');
    const lastPart = parts[parts.length - 1];
    const num = parseInt(lastPart, 10);
    // eslint-disable-next-line no-restricted-globals
    return isNaN(num) ? 0 : num;
  };

  const getSubtitlesForGroup = () => {
    const childTactics = tactics?.filter((tacticItem) => tacticItem?.parentTactic?._id === tactic._id)
      ?.sort((a: Tactic, b: Tactic) => {
        return extractLastNumber(a.formattedTacticId) - extractLastNumber(b.formattedTacticId);
      });

    const subtitles = childTactics?.map((tacticItem) => tacticItem?.title || '');
    return subtitles;
  };

  const getIconByChannel = () => {
    if (!tactic?.channel) return null;

    const currentChannel = Channel[tactic?.channel];

    switch (currentChannel) {
      case Channel.oneToOneDropMaterial:
      case Channel.oneToOneIVA:
      case Channel.oneToOneKeyMessage:
      case Channel.oneToOne:
        return Person;
      case Channel.f2fMeeting:
        return Person;
      case Channel.f2fEvent:
        return QuestionMark;
      case Channel.telephone:
        return Phone;
      case Channel.broadcastEmail:
        return AllInbox;
      case Channel.virtualEvent:
        return Event;
      case Channel.approvedEmail:
        return Email;
      case Channel.virtualMeeting:
        return Email;
      case Channel.hybridEvent:
        return Event;
      case Channel.webLogIn:
        return Http;
      case Channel.webNoLogIn:
        return Http;
      case Channel.group:
        return AltRoute;
      default:
        return QuestionMark;
    }
  };

  const { tacticCardConfig } = useMiViewTacticCardConfig();

  return {
    formatDateToMMDDYYYY,
    getSubtitle,
    getChannel,
    getIconByChannel,
    tacticCardConfig,
    getSubtitlesForGroup,
  };
};

export default useMiViewTacticCard;
